import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { effect, Injectable, InjectionToken, inject, PLATFORM_ID } from '@angular/core';
import { environment } from 'src/environments/environment';
import { ConsentChoice } from './consent.model';
import { ConsentService } from './consent.service';

/**
 * The Google tag a market injects once consent is granted. A market carries at
 * most one: a GTM container takes precedence, and the GA4 id is only read when
 * no container is set. Both empty means "never inject a tag".
 */
export interface AnalyticsTagConfig {
  readonly gaMeasurementId: string;
  readonly gtmContainerId: string;
}

// Overridable in tests; in a build it resolves from the (file-replaced) environment.
export const ANALYTICS_TAG_CONFIG = new InjectionToken<AnalyticsTagConfig>('ANALYTICS_TAG_CONFIG', {
  providedIn: 'root',
  factory: (): AnalyticsTagConfig => ({
    gaMeasurementId: environment.gaMeasurementId,
    gtmContainerId: environment.gtmContainerId,
  }),
});

type Gtag = (...args: unknown[]) => void;
type TaggedWindow = Window & { dataLayer?: unknown[]; gtag?: Gtag };
type ConsentState = 'granted' | 'denied';

const GOOGLE_COOKIE_NAMES = ['_ga', '_gid'] as const;
const GOOGLE_COOKIE_PREFIXES = ['_ga_', '_gat'] as const;
// The Meta Pixel's browser id and click id. Set by the pixel tag inside GTM, so
// they are ours to expire when marketing consent is withdrawn.
const META_COOKIE_NAMES = ['_fbp', '_fbc'] as const;

/**
 * Loads the market's Google tag only once consent has been granted, and only
 * on markets running our own banner. Nothing Google-owned is requested, and no
 * gtag stub exists, until then: there is deliberately no static tag in any
 * market's index.html, so a visitor who never consents never touches
 * googletagmanager.
 *
 * Consent mode is primed before the tag loads, denied by default and then
 * updated to the visitor's actual choice, so the very first hit carries the
 * consent state rather than inheriting Google's default. With a GTM container
 * that also means every tag inside it sees the consent state before it can
 * fire: analytics tags read `analytics_storage`, ad tags such as the Meta
 * Pixel read `ad_storage`.
 *
 * The consent state is fixed for the life of a page. A later change expires
 * the cookies of whatever was withdrawn and reloads, after which the tag is
 * loaded again (or not at all) under the new state. Reloading is what makes a
 * change reliable: a running tag cannot be unloaded, and GTM does not re-fire
 * a blocked tag merely because consent was granted afterwards.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsLoaderService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject<Document>(DOCUMENT);
  private readonly consent = inject(ConsentService);
  private readonly tags = inject(ANALYTICS_TAG_CONFIG);

  /** The consent the tag was loaded under, or null while nothing is loaded. */
  private loadedWith: ConsentChoice | null = null;

  constructor() {
    if (!isPlatformBrowser(this.platformId) || !this.consent.selfHosted) {
      return;
    }

    effect(() => {
      const choice: ConsentChoice = {
        analytics: this.consent.analyticsGranted(),
        marketing: this.consent.marketingGranted(),
      };
      if (this.loadedWith === null) {
        if (this.wantsTag(choice)) this.load(choice);
      } else if (!sameChoice(this.loadedWith, choice)) {
        this.reloadUnder(choice);
      }
    });
  }

  /**
   * Whether this market's tag has anything to do under the given consent. A
   * bare GA4 tag is analytics only, so marketing consent alone does not load
   * it; a GTM container also carries the ad tags, so either category does.
   */
  private wantsTag(choice: ConsentChoice): boolean {
    if (this.tags.gtmContainerId) return choice.analytics || choice.marketing;
    if (this.tags.gaMeasurementId) return choice.analytics;
    return false;
  }

  private load(choice: ConsentChoice): void {
    const view = this.document.defaultView as TaggedWindow | null;
    if (!view) return;
    this.loadedWith = choice;

    const gtag = this.primeConsent(view, choice);
    if (this.tags.gtmContainerId) {
      this.loadGtm(view, this.tags.gtmContainerId);
    } else {
      this.loadGtag(gtag, this.tags.gaMeasurementId);
    }
  }

  /**
   * Publishes Google's own gtag stub on window and queues the consent commands.
   * The stub has to push the `arguments` object rather than a rest array:
   * gtag.js and gtm.js both read that exact shape, so the two are not
   * interchangeable. Published as window.gtag, the name the standard snippet
   * defines and the name any future event call will reach for; it only ever
   * queues into dataLayer, so a call made before the tag loads is harmless.
   */
  private primeConsent(view: TaggedWindow, choice: ConsentChoice): Gtag {
    view.dataLayer = view.dataLayer ?? [];
    const dataLayer = view.dataLayer;
    const gtag = function (): void {
      // biome-ignore lint/complexity/noArguments: gtag.js reads the arguments object
      dataLayer.push(arguments);
    } as unknown as Gtag;
    view.gtag = gtag;

    gtag('consent', 'default', {
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      analytics_storage: 'denied',
    });
    const marketing = consentState(choice.marketing);
    gtag('consent', 'update', {
      ad_storage: marketing,
      ad_user_data: marketing,
      ad_personalization: marketing,
      analytics_storage: consentState(choice.analytics),
    });
    return gtag;
  }

  private loadGtag(gtag: Gtag, measurementId: string): void {
    gtag('js', new Date());
    gtag('config', measurementId);
    this.appendScript(`https://www.googletagmanager.com/gtag/js?id=${measurementId}`);
  }

  /** Google's standard container snippet, minus the script insertion it does itself. */
  private loadGtm(view: TaggedWindow, containerId: string): void {
    view.dataLayer?.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
    this.appendScript(`https://www.googletagmanager.com/gtm.js?id=${containerId}`);
  }

  private appendScript(src: string): void {
    const script = this.document.createElement('script');
    script.async = true;
    script.src = src;
    this.document.head.appendChild(script);
  }

  private reloadUnder(choice: ConsentChoice): void {
    const before = this.loadedWith;
    if (before?.analytics && !choice.analytics) {
      this.expireCookies((name) => isGoogleCookie(name));
    }
    if (before?.marketing && !choice.marketing) {
      this.expireCookies((name) => isMetaCookie(name));
    }
    this.document.defaultView?.location.reload();
  }

  private expireCookies(matches: (name: string) => boolean): void {
    const hostname = this.document.defaultView?.location.hostname ?? '';
    // Both the exact host and the dot-prefixed registrable domain: the tags
    // write on the latter, and a cookie only clears when the domain matches
    // how it was set.
    const domains = ['', hostname, `.${hostname.replace(/^www\./, '')}`];

    for (const cookie of this.document.cookie.split('; ')) {
      const name = cookie.split('=')[0];
      if (!matches(name)) continue;
      for (const domain of domains) {
        const domainPart = domain ? `; domain=${domain}` : '';
        this.document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domainPart}`;
      }
    }
  }
}

function consentState(granted: boolean): ConsentState {
  return granted ? 'granted' : 'denied';
}

function sameChoice(a: ConsentChoice, b: ConsentChoice): boolean {
  return a.analytics === b.analytics && a.marketing === b.marketing;
}

function isGoogleCookie(name: string): boolean {
  return (
    GOOGLE_COOKIE_NAMES.some((known) => known === name) ||
    GOOGLE_COOKIE_PREFIXES.some((prefix) => name.startsWith(prefix))
  );
}

function isMetaCookie(name: string): boolean {
  return META_COOKIE_NAMES.some((known) => known === name);
}
